/* School search — type-ahead over the schools directory rendered by
   snippets/school-search.liquid ([data-ss-item] rows carry name / url / tier /
   crest). Progressive: without this file the form is a plain product search.

   - Token match: every word typed must appear in the row's haystack
     (name, name without apostrophes, stage label). Punctuation-insensitive.
   - Scope: on a stage page (data-scope = nursery / primary / grammar) only
     that stage's schools are listed, no stage label. Other stages are offered
     only when the stage itself has no match — under a hint line
     (data-other-hint), each row labelled with its stage — so a parent who
     typed "Wallace" on the prep page sees Wallace Prep, not the High School,
     while a nursery name typed there still leads somewhere.
   - Ranking: name starts with the query → A–Z. Up to 8 shown.
   - Enter: opens the highlighted result, else the first result; with no
     match the form submits natively to /search?q=.
   - Keyboard ↑ ↓ Enter Esc, ARIA combobox/listbox, aria-activedescendant.
   - Typing also filters the "Browse all schools" list in place. */
(function () {
  'use strict';

  function norm(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[’'`]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function highlight(name, q) {
    if (!q) return escapeHtml(name);
    var i = name.toLowerCase().indexOf(q.toLowerCase());
    if (i === -1) {
      // Try the first token (query may be "pond park primary")
      var first = q.split(' ')[0];
      i = first ? name.toLowerCase().indexOf(first) : -1;
      if (i === -1) return escapeHtml(name);
      q = first;
    }
    return escapeHtml(name.slice(0, i)) + '<mark>' + escapeHtml(name.slice(i, i + q.length)) + '</mark>' + escapeHtml(name.slice(i + q.length));
  }

  function init(root) {
    var form = root.querySelector('[data-ss-form]');
    var input = root.querySelector('[data-ss-input]');
    var results = root.querySelector('[data-ss-results]');
    var empty = root.querySelector('[data-ss-empty]');
    var emptyQ = root.querySelector('[data-ss-q]');
    var emptyLink = root.querySelector('[data-ss-search-link]');
    var clearBtn = root.querySelector('[data-ss-clear]');
    var browse = root.querySelector('[data-ss-browse]');
    var scope = root.getAttribute('data-scope') || 'all';
    var otherHint = root.getAttribute('data-other-hint') || '';
    var fallback = false; // true when the list shows other stages because ours had no match
    if (!form || !input || !results) return;

    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-ss-item]'));
    var data = rows.map(function (el) {
      var name = el.getAttribute('data-name') || '';
      return {
        el: el,
        name: name,
        nname: norm(name).replace(/^the /, ''),
        url: el.getAttribute('data-url') || '#',
        tier: el.getAttribute('data-tier') || '',
        tierLabel: el.getAttribute('data-tier-label') || '',
        note: el.getAttribute('data-note') || '',
        crest: el.getAttribute('data-crest') || '',
        crest2x: el.getAttribute('data-crest-2x') || '',
        initial: el.getAttribute('data-initial') || name.charAt(0),
        haystack: norm(el.getAttribute('data-haystack') || name)
      };
    });
    if (!data.length) return; // menu missing → plain search form

    var current = [];
    var activeIndex = -1;
    var groups = browse ? Array.prototype.slice.call(browse.querySelectorAll('[data-ss-group]')) : [];

    function setExpanded(open) {
      input.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function close() {
      results.hidden = true;
      results.innerHTML = '';
      if (empty) empty.hidden = true;
      setExpanded(false);
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      current = [];
    }

    function match(q) {
      var tokens = q.split(' ').filter(Boolean);
      var hits = data.filter(function (d) {
        for (var i = 0; i < tokens.length; i++) {
          if (d.haystack.indexOf(tokens[i]) === -1) return false;
        }
        return true;
      });
      fallback = false;
      if (scope !== 'all') {
        var mine = hits.filter(function (d) { return d.tier === scope; });
        if (mine.length) hits = mine;
        else fallback = hits.length > 0;
      }
      return hits.sort(function (a, b) {
        var ap = a.nname.indexOf(q) === 0 ? 0 : 1;
        var bp = b.nname.indexOf(q) === 0 ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return a.nname.localeCompare(b.nname);
      });
    }

    function filterBrowse(q) {
      if (!browse) return;
      if (!q) {
        data.forEach(function (d) { d.el.classList.remove('is-hidden'); });
        groups.forEach(function (g) { g.hidden = false; });
        return;
      }
      var tokens = q.split(' ').filter(Boolean);
      data.forEach(function (d) {
        var ok = tokens.every(function (t) { return d.haystack.indexOf(t) !== -1; });
        d.el.classList.toggle('is-hidden', !ok);
      });
      groups.forEach(function (g) {
        g.hidden = !g.querySelector('[data-ss-item]:not(.is-hidden)');
      });
    }

    function render(raw) {
      var q = norm(raw);
      filterBrowse(q);
      if (!q) { close(); return; }
      current = match(q).slice(0, 8);
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');

      if (!current.length) {
        results.hidden = true;
        results.innerHTML = '';
        setExpanded(false);
        if (empty) {
          if (emptyQ) emptyQ.textContent = raw.trim();
          if (emptyLink) {
            var base = emptyLink.getAttribute('data-base') || emptyLink.getAttribute('href').split('?')[0];
            emptyLink.setAttribute('data-base', base);
            emptyLink.setAttribute('href', base + '?type=product&q=' + encodeURIComponent(raw.trim()));
          }
          empty.hidden = false;
        }
        return;
      }

      if (empty) empty.hidden = true;
      var hint = fallback && otherHint
        ? '<li class="school-search__result-hint" role="presentation">' + escapeHtml(otherHint) + '</li>'
        : '';
      results.innerHTML = hint + current.map(function (d, i) {
        var other = scope !== 'all' && d.tier !== scope;
        // Stage label only where it disambiguates: the hub, or a fallback row.
        var showMeta = scope === 'all' || other;
        var crest = d.crest
          ? '<img class="school-search__crest" src="' + escapeHtml(d.crest) + '"' + (d.crest2x ? ' srcset="' + escapeHtml(d.crest) + ' 1x, ' + escapeHtml(d.crest2x) + ' 2x"' : '') + ' width="40" height="40" alt="" loading="lazy" onerror="var s=document.createElement(\'span\');s.className=\'school-search__crest school-search__crest--placeholder\';s.setAttribute(\'aria-hidden\',\'true\');s.textContent=\'' + escapeHtml(d.initial).replace(/'/g, '') + '\';this.replaceWith(s)">'
          : '<span class="school-search__crest school-search__crest--placeholder" aria-hidden="true">' + escapeHtml(d.initial) + '</span>';
        var note = d.note ? '<span class="school-search__result-note">' + escapeHtml(d.note) + '</span>' : '';
        var meta = showMeta && d.tierLabel ? '<span class="school-search__result-meta">' + escapeHtml(d.tierLabel) + '</span>' : '';
        return '<li class="school-search__result' + (other ? ' school-search__result--other' : '') + '" role="option" id="school-search-opt-' + i + '" aria-selected="false">' +
                 '<a href="' + escapeHtml(d.url) + '">' + crest +
                   '<span><span class="school-search__result-name">' + highlight(d.name, raw.trim()) + '</span>' + note + '</span>' +
                   meta +
                 '</a></li>';
      }).join('');
      results.hidden = false;
      setExpanded(true);
    }

    function setActive(idx) {
      var opts = results.querySelectorAll('.school-search__result');
      Array.prototype.forEach.call(opts, function (o) { o.classList.remove('is-active'); o.setAttribute('aria-selected', 'false'); });
      if (idx < 0 || idx >= opts.length) { activeIndex = -1; input.removeAttribute('aria-activedescendant'); return; }
      activeIndex = idx;
      opts[idx].classList.add('is-active');
      opts[idx].setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', 'school-search-opt-' + idx);
      if (opts[idx].scrollIntoView) opts[idx].scrollIntoView({ block: 'nearest' });
    }

    function go(d) {
      if (d && d.url) window.location.href = d.url;
    }

    input.addEventListener('input', function () {
      var raw = input.value;
      if (clearBtn) clearBtn.hidden = raw.trim().length === 0;
      render(raw);
    });

    input.addEventListener('focus', function () {
      if (input.value.trim() && !current.length) render(input.value);
      else if (current.length) { results.hidden = false; setExpanded(true); }
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (input.value) { input.value = ''; if (clearBtn) clearBtn.hidden = true; }
        close();
        filterBrowse('');
        return;
      }
      if (results.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(Math.min(activeIndex + 1, current.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(Math.max(activeIndex - 1, 0)); }
    });

    form.addEventListener('submit', function (e) {
      // Enter / Search button: highlighted result, else the top match. No
      // match at all → let the native /search request through.
      if (!input.value.trim()) { e.preventDefault(); input.focus(); return; }
      if (current.length) {
        e.preventDefault();
        go(current[activeIndex >= 0 ? activeIndex : 0]);
      }
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        clearBtn.hidden = true;
        close();
        filterBrowse('');
        input.focus();
      });
    }

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target) && !results.hidden) { results.hidden = true; setExpanded(false); }
    });

    // "+N more" crest tile → open the A–Z <details> and glide to it (the
    // anchor alone would land on a closed summary).
    var more = root.querySelector('[data-ss-more]');
    if (more && browse) {
      more.addEventListener('click', function (e) {
        e.preventDefault();
        if (browse.tagName === 'DETAILS') browse.open = true;
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        browse.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      });
    }

    // Prefill from ?school= (e.g. links back from the contact page) — purely a convenience.
    try {
      var params = new URLSearchParams(window.location.search);
      var pre = params.get('school');
      if (pre && !input.value) { input.value = pre; render(pre); if (clearBtn) clearBtn.hidden = false; }
    } catch (err) { /* ignore */ }
  }

  function boot() {
    document.querySelectorAll('[data-school-search]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
